import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { GoogleSignInButton, AuthOrDivider, GOOGLE_ENABLED } from '@/auth/GoogleSignInButton';
import {
  type ContactFormValues,
  EMPTY_CONTACT_FORM,
} from '@/lib/salesForm/fieldMapping';
import { validateContactFields, type ValidationErrors } from '@/lib/salesForm/validation';
import { submitToGoogleForm } from '@/lib/salesForm/googleFormSubmit';
import './Login.css'; // re-uses Login card styling
import './Register.css'; // extra field types + wizard styling

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
);
const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68"/><path d="M6.06 6.06C3.4 7.7 2 12 2 12s3.5 7 10 7a9.7 9.7 0 0 0 3.5-.64"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="M2 2l20 20"/></svg>
);

const TOTAL_STEPS = 3;

// Which lead fields each wizard step is responsible for validating.
const STEP1_FIELDS: (keyof ValidationErrors)[] = ['name', 'email', 'contactNo', 'whoAreYou'];
const STEP2_FIELDS: (keyof ValidationErrors)[] = [
  'individualJobRole', 'schoolName', 'stream', 'yearsOfExperience', 'codingLanguages',
  'orgName', 'orgSize', 'orgJobRole',
];

function pickErrors(all: ValidationErrors, keys: (keyof ValidationErrors)[]): ValidationErrors {
  const out: ValidationErrors = {};
  for (const k of keys) {
    const v = all[k];
    if (v) out[k] = v;
  }
  return out;
}

/** Signup intent carried from the landing pricing CTA (?plan&cycle&coupon&ref).
 *  Read from the URL, falling back to sessionStorage so it survives the Google
 *  sign-in popup/redirect. Plain hints — re-validated server-side at checkout. */
function readIntent(): { plan?: string | undefined; cycle?: string | undefined; coupon?: string | undefined; ref?: string | undefined } {
  try {
    const q = new URLSearchParams(window.location.search);
    const fromUrl = {
      plan: q.get('plan') ?? undefined,
      cycle: q.get('cycle') ?? undefined,
      coupon: q.get('coupon') ?? undefined,
      ref: q.get('ref') ?? undefined,
    };
    if (fromUrl.plan || fromUrl.coupon) {
      sessionStorage.setItem('brew.intent', JSON.stringify(fromUrl));
      return fromUrl;
    }
    const stored = sessionStorage.getItem('brew.intent');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function Register() {
  const auth = useAuth();
  const navigate = useNavigate();
  // Captured once on mount (before any Google popup) so the plan/offer the visitor
  // chose survives signup.
  const [intent] = useState(readIntent);

  /** After signup, send a plan-carrying visitor to the pay-now/pay-later screen
   *  (it reads the server-recorded intent); everyone else to the app home. */
  function postSignupTarget(): string {
    return intent.plan ? '/welcome' : '/';
  }

  /** The signup intent forwarded to the server (maps the URL 'plan' → planName),
   *  so it records the OnboardingIntent the /welcome screen reads back. */
  function signupIntent() {
    return intent.plan
      ? { planName: intent.plan, cycle: intent.cycle, couponCode: intent.coupon, ref: intent.ref }
      : undefined;
  }

  const [step, setStep] = useState(1);
  const [values, setValues] = useState<ContactFormValues>(EMPTY_CONTACT_FORM);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [waitlisted, setWaitlisted] = useState(false);

  const isIndividual = values.whoAreYou === 'Individual';
  const isOrganization = values.whoAreYou === 'Organization';
  const isStudent = isIndividual && values.individualJobRole === 'Active Student';
  const isDeveloper = isIndividual && values.individualJobRole === 'Developer';

  function setField<K extends keyof ContactFormValues>(key: K, value: ContactFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key as keyof ValidationErrors]) {
      setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  }

  function handleWhoAreYou(who: 'Individual' | 'Organization') {
    // Reset every conditional field when the path changes.
    setValues((prev) => ({
      ...prev,
      whoAreYou: who,
      individualJobRole: '',
      schoolName: '',
      stream: '',
      yearsOfExperience: '',
      codingLanguages: '',
      orgName: '',
      orgSize: '',
      orgJobRole: '',
      orgWebsite: '',
    }));
    setFieldErrors(({ whoAreYou: _c, ...rest }) => rest);
  }

  function handleIndividualJobRole(role: ContactFormValues['individualJobRole']) {
    setValues((prev) => ({
      ...prev,
      individualJobRole: role,
      schoolName: role === 'Active Student' ? (prev.schoolName ?? '') : '',
      stream: role === 'Active Student' ? (prev.stream ?? '') : '',
      yearsOfExperience: role === 'Developer' ? (prev.yearsOfExperience ?? '') : '',
      codingLanguages: role === 'Developer' ? (prev.codingLanguages ?? '') : '',
    }));
    setFieldErrors(({ individualJobRole: _c, ...rest }) => rest);
  }

  function goNext() {
    setErr(null);
    const all = validateContactFields(values);
    const stepErrors = pickErrors(all, step === 1 ? STEP1_FIELDS : STEP2_FIELDS);
    if (Object.keys(stepErrors).length) {
      setFieldErrors(stepErrors);
      return;
    }
    setFieldErrors({});
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  }

  function goBack() {
    setErr(null);
    setFieldErrors({});
    setStep((s) => Math.max(s - 1, 1));
  }

  // Skip the optional lead/survey fields. Only name + email are required to
  // create the account, so validate just those and jump straight to the
  // password step. Contact No., who-are-you and the whole step-2 survey are
  // left blank (they only feed the best-effort sales profile).
  function goSkip() {
    setErr(null);
    const essential = pickErrors(validateContactFields(values), ['name', 'email']);
    if (Object.keys(essential).length) {
      setFieldErrors(essential);
      setStep(1);
      return;
    }
    setFieldErrors({});
    setStep(TOTAL_STEPS);
  }

  // Google sign-up: one click creates the account (find-or-create on the server)
  // and drops the user straight into the app. NOTE: this bypasses the lead/sales
  // profile the manual wizard collects — see the salesProfile caveat.
  async function handleGoogleCredential(credential: string): Promise<void> {
    setErr(null);
    setSubmitting(true);
    try {
      const { waitlisted: onWaitlist } = await auth.loginWithGoogle(credential, signupIntent());
      if (onWaitlist) { setWaitlisted(true); return; }
      sessionStorage.removeItem('brew.intent');
      navigate(postSignupTarget(), { replace: true });
    } catch (e) {
      const apiErr = e as { status?: number; message?: string; body?: { error?: string; detail?: string } };
      setErr(apiErr.body?.detail || apiErr.message || 'Google sign-up failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function createAccount(): Promise<void> {
    setErr(null);
    // Re-check the essentials (name + email) — the only truly required fields —
    // so an invalid email (e.g. a bad TLD) is caught HERE with a clear per-field
    // message on step 1, instead of the server bouncing it as a generic error.
    const essential = pickErrors(validateContactFields(values), ['name', 'email']);
    if (Object.keys(essential).length) {
      setFieldErrors(essential);
      setStep(1);
      return;
    }
    const errors: ValidationErrors = {};
    if (password.length < 8) errors.password = 'Password must be at least 8 characters.';
    if (password !== confirm) errors.confirm = 'Passwords do not match.';
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setSubmitting(true);
    try {
      // 1) Create the account (persists salesProfile in our DB). Must succeed.
      //    Comes back waitlisted when the platform is at its seat cap.
      const { waitlisted: onWaitlist } = await auth.register(
        values.email, password, values.name, { ...values }, signupIntent(),
      );

      // 2) Best-effort: forward the lead to the sales team's Google Form. Sent
      //    for waitlisted signups too — sales wants those leads either way.
      //    Never blocks or fails signup, and never includes the password.
      try {
        await submitToGoogleForm(values);
      } catch {
        /* sales notification is best-effort — ignore failures */
      }

      if (onWaitlist) {
        setWaitlisted(true);
        return;
      }
      sessionStorage.removeItem('brew.intent');
      navigate(postSignupTarget(), { replace: true });
    } catch (e) {
      const apiErr = e as { status?: number; message?: string };
      if (apiErr.status === 409) {
        setErr('An account with this email already exists. Sign in instead.');
      } else if (apiErr.status === 400) {
        setErr('Some fields are invalid. Check your inputs.');
      } else {
        setErr(apiErr.message || 'Sign-up failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Enter / form submit routes to "Next" until the last step.
  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (step < TOTAL_STEPS) {
      goNext();
      return;
    }
    void createAccount();
  }

  // Seat cap reached — the account exists but can't sign in until an admin
  // opens a seat. Terminal screen: no form, no way into the app.
  if (waitlisted) {
    return (
      <div className="login">
        <div className="login__card login__card--wide">
          <header className="login__header">
            <img className="login__logo" src="/brew-logo.png" alt="Breww.ai" width={108} height={108} />
          </header>
          <div className="reg__waitlist">
            <div className="reg__waitlist-badge" aria-hidden="true">⏳</div>
            <h1>You're on the waiting list</h1>
            <p>
              Thanks {values.name.trim().split(' ')[0] || 'for signing up'} — Breww.ai is at
              capacity right now, so we've saved your spot. We'll open access for{' '}
              <strong>{values.email}</strong> as soon as a seat frees up.
            </p>
            <p className="reg__waitlist-note">
              You won't be able to sign in until then. Our team has your details and will be in
              touch.
            </p>
            <Link className="login__link login__link--accent" to="/login">Back to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <div className="login__card login__card--wide">
        <header className="login__header">
          <img className="login__logo" src="/brew-logo.png" alt="Breww.ai" width={108} height={108} />
        </header>

        {/* Step indicator */}
        <div className="reg__steps" aria-label={`Step ${step} of ${TOTAL_STEPS}`}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <span
              key={i}
              className={`reg__dot${i + 1 === step ? ' reg__dot--active' : ''}${i + 1 < step ? ' reg__dot--done' : ''}`}
            />
          ))}
        </div>

        <form onSubmit={handleSubmit} className="login__form" noValidate>
          {/* ── Step 1: basics + who-are-you ─────────────────────────── */}
          {step === 1 && (
            <div className="reg__step" key="step1">
              <label className="login__field">
                <span>Full name</span>
                <input
                  type="text"
                  autoComplete="name"
                  value={values.name}
                  onChange={(e) => setField('name', e.target.value)}
                  required
                  autoFocus
                />
                {fieldErrors.name && <em className="reg__error">{fieldErrors.name}</em>}
              </label>

              <label className="login__field">
                <span>Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={values.email}
                  onChange={(e) => setField('email', e.target.value)}
                  required
                />
                {fieldErrors.email && <em className="reg__error">{fieldErrors.email}</em>}
              </label>

              <label className="login__field">
                <span>Contact No.</span>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="e.g. +91 98765 43210"
                  value={values.contactNo}
                  onChange={(e) => setField('contactNo', e.target.value)}
                  required
                />
                {fieldErrors.contactNo && <em className="reg__error">{fieldErrors.contactNo}</em>}
              </label>

              <fieldset className="reg__fieldset">
                <legend>Who are you?</legend>
                <div className="reg__radio-row">
                  {(['Individual', 'Organization'] as const).map((opt) => (
                    <label key={opt} className="reg__radio">
                      <input
                        type="radio"
                        name="whoAreYou"
                        value={opt}
                        checked={values.whoAreYou === opt}
                        onChange={() => handleWhoAreYou(opt)}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
                {fieldErrors.whoAreYou && <em className="reg__error">{fieldErrors.whoAreYou}</em>}
              </fieldset>
            </div>
          )}

          {/* ── Step 2: details for the chosen path ──────────────────── */}
          {step === 2 && (
            <div className="reg__step" key="step2">
              {isIndividual && (
                <>
                  <fieldset className="reg__fieldset">
                    <legend>Job Role</legend>
                    <div className="reg__radio-row">
                      {(['Active Student', 'Developer', 'Other'] as const).map((role) => (
                        <label key={role} className="reg__radio">
                          <input
                            type="radio"
                            name="individualJobRole"
                            value={role}
                            checked={values.individualJobRole === role}
                            onChange={() => handleIndividualJobRole(role)}
                          />
                          <span>{role}</span>
                        </label>
                      ))}
                    </div>
                    {fieldErrors.individualJobRole && <em className="reg__error">{fieldErrors.individualJobRole}</em>}
                  </fieldset>

                  {isStudent && (
                    <>
                      <label className="login__field">
                        <span>Your School / College / University</span>
                        <input
                          type="text"
                          value={values.schoolName || ''}
                          onChange={(e) => setField('schoolName', e.target.value)}
                          placeholder="e.g. Stanford University"
                        />
                        {fieldErrors.schoolName && <em className="reg__error">{fieldErrors.schoolName}</em>}
                      </label>
                      <label className="login__field">
                        <span>Your Stream</span>
                        <input
                          type="text"
                          value={values.stream || ''}
                          onChange={(e) => setField('stream', e.target.value)}
                          placeholder="e.g. Computer Science"
                        />
                        {fieldErrors.stream && <em className="reg__error">{fieldErrors.stream}</em>}
                      </label>
                    </>
                  )}

                  {isDeveloper && (
                    <>
                      <fieldset className="reg__fieldset">
                        <legend>Years of Experience</legend>
                        <div className="reg__radio-row">
                          {(['0-2 years', '3-6 years', '6+ years'] as const).map((opt) => (
                            <label key={opt} className="reg__radio">
                              <input
                                type="radio"
                                name="yearsOfExperience"
                                value={opt}
                                checked={values.yearsOfExperience === opt}
                                onChange={() => setField('yearsOfExperience', opt)}
                              />
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                        {fieldErrors.yearsOfExperience && <em className="reg__error">{fieldErrors.yearsOfExperience}</em>}
                      </fieldset>
                      <label className="login__field">
                        <span>What Coding Language do you use?</span>
                        <select
                          value={values.codingLanguages || ''}
                          onChange={(e) => setField('codingLanguages', e.target.value)}
                          className="reg__select"
                        >
                          <option value="">Select a language</option>
                          {['Python', 'Java', 'JavaScript', 'TypeScript', 'C++', 'NextJS', 'SQL', 'React', 'Swift'].map((lang) => (
                            <option key={lang} value={lang}>{lang}</option>
                          ))}
                        </select>
                        {fieldErrors.codingLanguages && <em className="reg__error">{fieldErrors.codingLanguages}</em>}
                      </label>
                    </>
                  )}
                </>
              )}

              {isOrganization && (
                <>
                  <label className="login__field">
                    <span>Organization Name</span>
                    <input
                      type="text"
                      autoComplete="organization"
                      value={values.orgName || ''}
                      onChange={(e) => setField('orgName', e.target.value)}
                      placeholder="e.g. Breww Corp"
                    />
                    {fieldErrors.orgName && <em className="reg__error">{fieldErrors.orgName}</em>}
                  </label>

                  <fieldset className="reg__fieldset">
                    <legend>Organization Size</legend>
                    <div className="reg__radio-row">
                      {(['1-10', '11-50', '51-100', '101-250', '250+'] as const).map((opt) => (
                        <label key={opt} className="reg__radio">
                          <input
                            type="radio"
                            name="orgSize"
                            value={opt}
                            checked={values.orgSize === opt}
                            onChange={() => setField('orgSize', opt)}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                    {fieldErrors.orgSize && <em className="reg__error">{fieldErrors.orgSize}</em>}
                  </fieldset>

                  <fieldset className="reg__fieldset">
                    <legend>Job Role</legend>
                    <div className="reg__radio-row">
                      {(['Owner', 'C-Level', 'Board Member', 'Director', 'Manager', 'Employee', 'Other'] as const).map((role) => (
                        <label key={role} className="reg__radio">
                          <input
                            type="radio"
                            name="orgJobRole"
                            value={role}
                            checked={values.orgJobRole === role}
                            onChange={() => setField('orgJobRole', role)}
                          />
                          <span>{role}</span>
                        </label>
                      ))}
                    </div>
                    {fieldErrors.orgJobRole && <em className="reg__error">{fieldErrors.orgJobRole}</em>}
                  </fieldset>

                  <label className="login__field">
                    <span>Organization Website</span>
                    <input
                      type="text"
                      value={values.orgWebsite || ''}
                      onChange={(e) => setField('orgWebsite', e.target.value)}
                      placeholder="e.g. https://company.com"
                    />
                    {fieldErrors.orgWebsite && <em className="reg__error">{fieldErrors.orgWebsite}</em>}
                  </label>
                </>
              )}
            </div>
          )}

          {/* ── Step 3: passwords ────────────────────────────────────── */}
          {step === 3 && (
            <div className="reg__step" key="step3">
              <label className="login__field">
                <span>Password</span>
                <div className="login__input-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    className="login__eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                  </button>
                </div>
                {fieldErrors.password && <em className="reg__error">{fieldErrors.password}</em>}
                {/* #5 — show the rule INLINE and live, so the user meets it before
                    submitting instead of being told only after a failed attempt. */}
                <ul className="reg__pw-reqs" aria-live="polite">
                  <li className={`reg__pw-req${password.length >= 8 ? ' is-ok' : ''}`}>
                    <span className="reg__pw-mark" aria-hidden="true">{password.length >= 8 ? '✓' : '○'}</span>
                    At least 8 characters
                  </li>
                </ul>
              </label>

              <label className="login__field">
                <span>Confirm password</span>
                <div className="login__input-wrap">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="login__eye"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    aria-pressed={showConfirm}
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeIcon /> : <EyeOffIcon />}
                  </button>
                </div>
                {fieldErrors.confirm && <em className="reg__error">{fieldErrors.confirm}</em>}
                {confirm.length > 0 && (
                  <ul className="reg__pw-reqs" aria-live="polite">
                    <li className={`reg__pw-req ${password === confirm ? 'is-ok' : 'is-bad'}`}>
                      <span className="reg__pw-mark" aria-hidden="true">{password === confirm ? '✓' : '✕'}</span>
                      {password === confirm ? 'Passwords match' : 'Passwords don’t match yet'}
                    </li>
                  </ul>
                )}
              </label>
            </div>
          )}

          {err && <div className="login__error">{err}</div>}

          <div className="reg__nav">
            {step > 1 && (
              <button type="button" className="reg__btn-back" onClick={goBack} disabled={submitting}>
                Back
              </button>
            )}
            {step < TOTAL_STEPS ? (
              <button type="submit" className="login__submit reg__btn-next">Next</button>
            ) : (
              <button type="submit" className="login__submit reg__btn-next" disabled={submitting}>
                {submitting ? 'Creating account…' : 'Create account'}
              </button>
            )}
          </div>

          {step < TOTAL_STEPS && (
            <button type="button" className="reg__btn-skip" onClick={goSkip} disabled={submitting}>
              Skip for now — just create my account
            </button>
          )}
        </form>

        {step === 1 && GOOGLE_ENABLED && (
          <>
            <AuthOrDivider label="or sign up with Google" />
            <GoogleSignInButton onCredential={handleGoogleCredential} text="signup_with" />
          </>
        )}

        <footer className="login__footer">
          <span className="login__sep">Already have an account?</span>
          <Link className="login__link login__link--accent" to="/login">Sign in</Link>
        </footer>
      </div>
    </div>
  );
}
